import { NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { LogOut, ClipboardCheck, UserCircle } from "lucide-react";

export default function AttendanceLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate("/attendance/login"); };
  const firstName = user?.name?.split(" ")?.[0] || user?.email?.split("@")?.[0];

  // Section leaders don't see the coord tabs; they only see their own dashboard.
  // Users who are BOTH a coord AND a section leader (e.g. small church where the
  // pastor's spouse coordinates AND leads a section) see both tabs.
  const isCoord = user && (user.is_admin || user.is_coordinator);
  const isLeader = user && user.is_section_leader;
  const isLeaderOnly = isLeader && !isCoord;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-base)" }} data-testid="attendance-layout">
      <header
        className="border-b sticky top-0 z-30 backdrop-blur"
        style={{
          borderColor: "var(--border-subtle)",
          background: "color-mix(in srgb, var(--bg-alt) 88%, transparent)",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between gap-4 flex-wrap">
          <Link to={isCoord ? "/attendance" : "/attendance/section"} data-testid="attendance-brand" className="flex flex-col leading-tight">
            <span className="font-serif-display text-xl lg:text-2xl tracking-[-0.03em]" style={{ color: "var(--ink)" }}>
              thedisciple<span style={{ color: "var(--terracotta)" }}>.attendance</span>
            </span>
            <span className="text-[0.55rem] uppercase tracking-[0.32em] mt-0.5 font-semibold" style={{ color: "var(--muted)" }}>
              Church Body Attendance
            </span>
          </Link>

          {user && (
            <div className="flex items-center gap-4 flex-wrap">
              <nav className="flex items-center gap-1 flex-wrap" data-testid="att-tabs">
                {isCoord && (
                  <NavLink
                    to="/attendance"
                    end
                    data-testid="att-tab-dashboard"
                    className={({ isActive }) =>
                      `flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full transition-colors ${
                        isActive ? "bg-[var(--ink)] text-[var(--bg-base)]" : "text-[var(--ink-2)] hover:bg-[var(--surface-hover)]"
                      }`}
                  >
                    <ClipboardCheck size={14} /> Attendance
                  </NavLink>
                )}
                {isLeader && (
                  <NavLink
                    to="/attendance/section"
                    end
                    data-testid="att-tab-section"
                    className={({ isActive }) =>
                      `flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full transition-colors ${
                        isActive ? "bg-[var(--ink)] text-[var(--bg-base)]" : "text-[var(--ink-2)] hover:bg-[var(--surface-hover)]"
                      }`}
                  >
                    <UserCircle size={14} /> My Section
                  </NavLink>
                )}
              </nav>
              <div className="flex items-center gap-3 pl-4 border-l" style={{ borderColor: "var(--border-subtle)" }}>
                {firstName && (
                  <span className="hidden sm:inline text-xs" style={{ color: "var(--muted)" }} data-testid="att-user-name">
                    Hi, <span style={{ color: "var(--ink)" }}>{firstName}</span>
                  </span>
                )}
                <button
                  onClick={handleLogout}
                  data-testid="att-logout-btn"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full transition-colors hover:bg-[var(--surface-hover)]"
                  style={{ color: "var(--terracotta)" }}
                >
                  <LogOut size={14} /> Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t py-4 text-xs text-center" style={{ borderColor: "var(--border-subtle)", color: "var(--muted)" }}>
        © {new Date().getFullYear()} The Disciple Life · thedisciple.attendance
      </footer>
    </div>
  );
}
